import { dotToComma } from '../../utils/FormatUtils'
import {
  onlyTwoDecimals,
  unitsTransformer,
  weightTransformer,
} from '../../utils/RecipeUtils'
import {
  Ingredient,
  Recipe,
  Season,
  Unbreakable,
} from '../../types/fam-graphql'
import { RecipeIngredientsProps } from '../../components/RecipeIngredientsSection'
import { colors } from '../../theme'

function isOutOfSeason(
  seasons?: Season[] | null,
  currentSeason?: Season | null
) {
  if (!seasons || !seasons.length || !currentSeason) {
    return false
  }
  return !seasons.includes(currentSeason)
}

export const ingredientsListTransformer = (
  recipePortions: number,
  people: number,
  ingredients: Ingredient[],
  currentSeason?: Season
): RecipeIngredientsProps[] => {
  const multiplicationFactor = people / recipePortions

  return ingredients?.map((ingredient: Ingredient) => {
    const isPartial = ingredient.unbreakable === Unbreakable.Partial ?? false
    let quantity =
      multiplicationFactor * (ingredient?.ingredient_recipe?.quantity ?? 0)

    if (isPartial) {
      quantity = Math.round(quantity)
    }

    quantity = onlyTwoDecimals(quantity)

    return {
      name: `${
        quantity >= 2
          ? ingredient?.plural ??
            ingredient?.display_plural ??
            ingredient?.display_name ??
            ingredient?.name
          : ingredient?.display_plural ??
            ingredient?.display_name ??
            ingredient?.name
      }${
        isOutOfSeason(ingredient.seasons, currentSeason) &&
        !ingredient?.frozen_or_canned
          ? '*'
          : ''
      }`,
      units: dotToComma(
        unitsTransformer(ingredient?.ingredient_recipe, quantity)
      ),
      weight: dotToComma(weightTransformer(ingredient, multiplicationFactor)),
    }
  })
}

type RecipeIngredientsTransformerArgs = {
  recipe: Recipe | null
  t: any
  ingredientsPeople: number
  onAddPersonHandler: () => void
  onRemovePersonHandler: () => void
  minPortions?: number | null
  currentSeason?: Season
}

export const recipeIngredientsDefaultTransformer = (
  args: RecipeIngredientsTransformerArgs
) => {
  const {
    recipe,
    t,
    ingredientsPeople,
    onAddPersonHandler,
    onRemovePersonHandler,
    minPortions,
    currentSeason,
  } = args
  return {
    title: t('recipe_ingredients_title'),
    textSeasonalMessage: recipe?.ingredients?.some((i) => {
      return (
        i?.seasons &&
        currentSeason &&
        isOutOfSeason(i.seasons, currentSeason) &&
        !i?.frozen_or_canned
      )
    })
      ? t('recipe_ingredients_seasonal_message')
      : '',
    textPeople: t('recipe_ingredients_person', {
      count: ingredientsPeople,
    }),
    textInsecable: t('recipe_ingredients_insecable_message'),
    isInsecable: recipe?.unbreakable === Unbreakable.Complete,
    numberPeople: ingredientsPeople,
    textPlus: t('recipe_ingredients_increment_person'),
    textMinus: t('recipe_ingredients_decrement_person'),
    numberMin: minPortions
      ? minPortions
      : recipe?.min_portions
      ? recipe?.min_portions
      : 1,
    numberMax: recipe?.max_portions ? recipe?.max_portions : 8,
    onAddPerson: onAddPersonHandler,
    onRemovePerson: onRemovePersonHandler,
    ingredients: ingredientsListTransformer(
      recipe?.portions ?? 0,
      ingredientsPeople,
      recipe?.ingredients ?? [],
      currentSeason
    ),
  }
}

export const recipeIngredientsForFamilyTransformer = (
  args: RecipeIngredientsTransformerArgs
) => {
  const defaultRecipeIngredientsProps = recipeIngredientsDefaultTransformer(
    args
  )

  return {
    ...defaultRecipeIngredientsProps,
    highlightTitleProps: {
      text: defaultRecipeIngredientsProps.title,
      backgroundColor: colors.familyYellow,
      color: colors.familyBurgundy,
    },
  }
}

export const recipeIngredientsTransformer = (
  args: RecipeIngredientsTransformerArgs
) => {
  if (args.recipe?.family_recipe) {
    return recipeIngredientsForFamilyTransformer(args)
  } else {
    return recipeIngredientsDefaultTransformer(args)
  }
}
